import React, { useState, useEffect, useMemo } from 'react';
import { getLlmUsageStats, getLlmUsageLogs } from '../../../utils/apiClient';
import { Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  ArcElement,
} from 'chart.js';
import 'chartjs-adapter-date-fns';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  ArcElement
);

const ApiUsageMetricsPage = () => {
  const [usageSummary, setUsageSummary] = useState(null);
  const [usageLogs, setUsageLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    days: 30,
    provider: '',
    task: '',
    limit: 100,
  });

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [summary, logs] = await Promise.all([
        getLlmUsageStats({ days: filters.days }),
        getLlmUsageLogs({
          limit: filters.limit,
          provider: filters.provider || undefined,
          task: filters.task || undefined,
        }),
      ]);
      setUsageSummary(summary);
      setUsageLogs(logs || []);
    } catch (err) {
      setError(err.message || 'Failed to fetch LLM usage data');
      setUsageSummary(null);
      setUsageLogs([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    fetchData();
  };

  // Chart data for usage over time
  const tokenChartData = useMemo(() => {
    if (!usageLogs || usageLogs.length === 0) {
      return { labels: [], datasets: [] };
    }

    // Group by date
    const groupedByDate = usageLogs.reduce((acc, log) => {
      const date = log.created_at.split('T')[0];
      if (!acc[date]) {
        acc[date] = { total_tokens: 0, cost: 0 };
      }
      acc[date].total_tokens += log.total_tokens || 0;
      acc[date].cost += log.estimated_cost_usd || 0;
      return acc;
    }, {});

    const sortedDates = Object.keys(groupedByDate).sort();

    return {
      labels: sortedDates,
      datasets: [
        {
          label: 'Total Tokens',
          data: sortedDates.map(d => groupedByDate[d].total_tokens),
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.3,
          fill: true,
          yAxisID: 'y',
        },
        {
          label: 'Cost (USD)',
          data: sortedDates.map(d => groupedByDate[d].cost),
          borderColor: 'rgb(34, 197, 94)',
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          tension: 0.3,
          fill: true,
          yAxisID: 'y1',
        },
      ],
    };
  }, [usageLogs]);

  // Provider breakdown chart
  const providerChartData = useMemo(() => {
    if (!usageSummary || !usageSummary.by_provider) {
      return { labels: [], datasets: [] };
    }

    const providers = Object.keys(usageSummary.by_provider);
    const tokens = providers.map(p => usageSummary.by_provider[p].total_tokens || 0);

    return {
      labels: providers,
      datasets: [{
        data: tokens,
        backgroundColor: ['rgba(59, 130, 246, 0.8)', 'rgba(168, 85, 247, 0.8)', 'rgba(34, 197, 94, 0.8)'],
        borderColor: ['rgb(59, 130, 246)', 'rgb(168, 85, 247)', 'rgb(34, 197, 94)'],
        borderWidth: 2,
      }],
    };
  }, [usageSummary]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    scales: {
      x: {
        type: 'category',
        title: { display: true, text: 'Date', color: '#cbd5e1' },
        ticks: { color: '#94a3b8' },
        grid: { color: '#374151' },
      },
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        beginAtZero: true,
        title: { display: true, text: 'Tokens', color: '#cbd5e1' },
        ticks: { color: '#94a3b8' },
        grid: { color: '#374151' },
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        beginAtZero: true,
        title: { display: true, text: 'Cost (USD)', color: '#cbd5e1' },
        ticks: { color: '#94a3b8' },
        grid: { drawOnChartArea: false },
      },
    },
    plugins: {
      legend: { position: 'top', labels: { color: '#e5e7eb' } },
      title: { display: true, text: 'LLM Usage Over Time', color: '#f3f4f6', font: { size: 16 } },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { color: '#e5e7eb' } },
      title: { display: true, text: 'Usage by Provider', color: '#f3f4f6' },
    },
  };

  const formatCost = (cost) => {
    if (cost === null || cost === undefined) return '$0.00';
    return `$${parseFloat(cost).toFixed(4)}`;
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0';
    return num.toLocaleString();
  };

  return (
    <div className="p-4 md:p-6 bg-gray-900 text-white min-h-screen">
      <h1 className="text-2xl md:text-3xl font-semibold mb-6 text-gray-100">LLM Usage Metrics</h1>

      {/* Filters */}
      <form onSubmit={handleFilterSubmit} className="mb-6 p-4 bg-gray-800 rounded-lg shadow-md grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
        <div>
          <label htmlFor="days" className="block text-sm font-medium text-gray-300">Time Period:</label>
          <select
            name="days"
            value={filters.days}
            onChange={handleFilterChange}
            className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-white shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm p-2"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>
        <div>
          <label htmlFor="provider" className="block text-sm font-medium text-gray-300">Provider:</label>
          <select
            name="provider"
            value={filters.provider}
            onChange={handleFilterChange}
            className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-white shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm p-2"
          >
            <option value="">All Providers</option>
            <option value="GROQ">Groq</option>
            <option value="DEEPSEEK">DeepSeek</option>
          </select>
        </div>
        <div>
          <label htmlFor="task" className="block text-sm font-medium text-gray-300">Task Type:</label>
          <input
            type="text"
            name="task"
            value={filters.task}
            onChange={handleFilterChange}
            placeholder="e.g., research_answer"
            className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-white shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm p-2"
          />
        </div>
        <div>
          <button
            type="submit"
            disabled={isLoading}
            className="w-full inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-gray-500 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Loading...' : 'Apply Filters'}
          </button>
        </div>
      </form>

      {error && <div className="my-4 p-3 bg-red-800/50 text-red-100 border border-red-700 rounded-md">Error: {error}</div>}

      {/* Summary Cards */}
      {usageSummary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-800 p-4 rounded-lg shadow-md">
            <div className="text-sm text-gray-400">Total Requests</div>
            <div className="text-2xl font-semibold text-blue-400">{formatNumber(usageSummary.total_requests)}</div>
          </div>
          <div className="bg-gray-800 p-4 rounded-lg shadow-md">
            <div className="text-sm text-gray-400">Total Tokens</div>
            <div className="text-2xl font-semibold text-purple-400">{formatNumber(usageSummary.total_tokens)}</div>
          </div>
          <div className="bg-gray-800 p-4 rounded-lg shadow-md">
            <div className="text-sm text-gray-400">Total Cost</div>
            <div className="text-2xl font-semibold text-green-400">{formatCost(usageSummary.total_cost_usd)}</div>
          </div>
          <div className="bg-gray-800 p-4 rounded-lg shadow-md">
            <div className="text-sm text-gray-400">Avg Latency</div>
            <div className="text-2xl font-semibold text-amber-400">
              {usageSummary.avg_latency_ms ? `${usageSummary.avg_latency_ms.toFixed(0)}ms` : 'N/A'}
            </div>
          </div>
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 bg-gray-800 p-4 rounded-lg shadow-md" style={{ height: '350px' }}>
          {isLoading && <p className="text-center text-gray-400">Loading chart...</p>}
          {!isLoading && usageLogs.length > 0 && <Line options={chartOptions} data={tokenChartData} />}
          {!isLoading && usageLogs.length === 0 && !error && <p className="text-center text-gray-400 pt-32">No usage data available yet.</p>}
        </div>
        <div className="bg-gray-800 p-4 rounded-lg shadow-md" style={{ height: '350px' }}>
          {!isLoading && usageSummary?.by_provider && Object.keys(usageSummary.by_provider).length > 0 && (
            <Doughnut options={doughnutOptions} data={providerChartData} />
          )}
          {(!usageSummary?.by_provider || Object.keys(usageSummary?.by_provider || {}).length === 0) && !isLoading && (
            <p className="text-center text-gray-400 pt-32">No provider data available.</p>
          )}
        </div>
      </div>

      {/* Usage Logs Table */}
      <h2 className="text-xl md:text-2xl font-semibold mb-4 mt-8 text-gray-100">Recent API Calls</h2>
      {isLoading && <p className="text-center text-gray-400">Loading data...</p>}
      {!isLoading && usageLogs.length > 0 && (
        <div className="overflow-x-auto bg-gray-800 rounded-lg shadow-md">
          <table className="min-w-full divide-y divide-gray-700">
            <thead className="bg-gray-700/50">
              <tr>
                {['Time', 'User', 'Provider', 'Model', 'Task', 'Tokens', 'Cost', 'Latency', 'Status'].map(header => (
                  <th key={header} className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">{header}</th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-gray-800 divide-y divide-gray-700">
              {usageLogs.map((log) => (
                <tr key={log.id} className="hover:bg-gray-700/50">
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-200">
                    {new Date(log.created_at).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-200">{log.user || 'System'}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${log.provider === 'GROQ' ? 'bg-blue-900 text-blue-300' :
                        log.provider === 'DEEPSEEK' ? 'bg-purple-900 text-purple-300' : 'bg-gray-700 text-gray-300'
                      }`}>
                      {log.provider}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-200">{log.model}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-200">{log.task || 'N/A'}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-200">{formatNumber(log.total_tokens)}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-green-400">{formatCost(log.estimated_cost_usd)}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-200">{log.latency_ms ? `${log.latency_ms}ms` : 'N/A'}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${log.success ? 'bg-green-900 text-green-300' : 'bg-red-900 text-red-300'
                      }`}>
                      {log.success ? 'Success' : 'Failed'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!isLoading && usageLogs.length === 0 && !error && (
        <div className="text-center py-8 bg-gray-800 rounded-lg">
          <p className="text-gray-400">No LLM usage logs yet. Usage will appear here once you start using AI features.</p>
        </div>
      )}
    </div>
  );
};

export default ApiUsageMetricsPage;
